import {
  DayOfWeekEnum,
  EmailScheduleEmailTypeEnum,
  type EmailSchedule as GQLItem,
  type EmailScheduleAttributes as GQLAttributes,
} from '../../generated/api.graphql';
import { take } from '../../util/takePayload';

export type Weekday = 0 | 1 | 2 | 3 | 4 | 5 | 6;

const WEEKDAY_TO_GQL: Record<Weekday, DayOfWeekEnum> = {
  0: DayOfWeekEnum.Sunday,
  1: DayOfWeekEnum.Monday,
  2: DayOfWeekEnum.Tuesday,
  3: DayOfWeekEnum.Wednesday,
  4: DayOfWeekEnum.Thursday,
  5: DayOfWeekEnum.Friday,
  6: DayOfWeekEnum.Saturday,
};

const GQL_TO_WEEKDAY: Record<DayOfWeekEnum, Weekday> = {
  [DayOfWeekEnum.Sunday]: 0,
  [DayOfWeekEnum.Monday]: 1,
  [DayOfWeekEnum.Tuesday]: 2,
  [DayOfWeekEnum.Wednesday]: 3,
  [DayOfWeekEnum.Thursday]: 4,
  [DayOfWeekEnum.Friday]: 5,
  [DayOfWeekEnum.Saturday]: 6,
};

export const weekdaysToGql = (weekdays: Weekday[]): DayOfWeekEnum[] =>
  weekdays.map(day => WEEKDAY_TO_GQL[day]);

export const weekdaysFromGql = (days: DayOfWeekEnum[]): Weekday[] =>
  [...days].map(day => GQL_TO_WEEKDAY[day]).sort((a, b) => a - b);

export const EmailScheduleEmailType = Object.freeze({
  SHAREABLE_REPORT: EmailScheduleEmailTypeEnum.ShareableReport,
});

export type EmailScheduleEmailType = (typeof EmailScheduleEmailType)[keyof typeof EmailScheduleEmailType];

export type EmailScheduleAttributes = {
  id?: number;
  eventId: number;
  emailType: EmailScheduleEmailType;
  hour: number;
  minute: number;
  weekdays?: Weekday[];
  startDate?: string;
  endDate?: string;
  to?: string[];
  cc?: string[];
  bcc?: string[];
};

export type EmailSchedule = {
  id: number;
  eventId: number;
  emailType: EmailScheduleEmailType;
  hour: number;
  minute: number;
  weekdays?: Weekday[];
  startDate?: string;
  endDate?: string;
  to?: string[];
  cc?: string[];
  bcc?: string[];
  lastSentAt?: string;
};

export const toGqlInput = (input: EmailScheduleAttributes): GQLAttributes => {
  const gqlInput = take<GQLAttributes>(
    input,
    'id',
    'eventId',
    'emailType',
    'hour',
    'minute',
    'startDate',
    'endDate',
    'to',
    'cc',
    'bcc',
  );

  if (input.weekdays?.length) {
    gqlInput.daysOfWeek = weekdaysToGql(input.weekdays);
  }

  return gqlInput;
};

export const fromGql = (item: GQLItem): EmailSchedule => {
  const schedule = take<EmailSchedule>(
    item,
    'id',
    'eventId',
    'emailType',
    'hour',
    'minute',
    'startDate',
    'endDate',
    'to',
    'cc',
    'bcc',
    'lastSentAt',
  );

  if (item.daysOfWeek?.length) {
    schedule.weekdays = weekdaysFromGql(item.daysOfWeek);
  }

  return schedule;
};

type EmailScheduleRecipients = Pick<EmailSchedule, 'to' | 'cc' | 'bcc'>;

export const recipientEmails = (schedule: EmailScheduleRecipients): string[] => [
  ...(schedule.to ?? []),
  ...(schedule.cc ?? []),
  ...(schedule.bcc ?? []),
];
